import React, { FC, useContext, useRef, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';

import DeleteNodeButton from 'src/presentation/components/pages/ivr/components/DeleteNodeButton';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import NodeList from 'src/presentation/components/pages/ivr/components/NodeList';
import SidePanelLayout from './SidePanelLayout';
import StartNode from 'src/presentation/components/pages/ivr/components/nodes/StartNode';
import TreeData from 'src/presentation/components/pages/ivr/components/ivrTree/models/TreeData';
import { IvrConfig } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import {
  CREATE_NEW_NODE,
  NO_VALUE_SELECTED_OPTION,
  NODE_COMPONENT_MAPPER,
  NODE_LIST_ID,
  SIDE_PANEL_ROW_TYPES,
  SIDE_PANEL_WIDTH,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';
import { NodeContainer, CreateNewOptionLabel } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { START_NODE_ID } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { SidePanelContext } from 'src/presentation/components/pages/ivr/IvrEditor';

const CREATE_NEW_OPTION = {
  label: <CreateNewOptionLabel>Create New Node</CreateNewOptionLabel>,
  value: CREATE_NEW_NODE,
  text: 'Create New Node',
};

export const getNodeListOptions = (nodes, { addNoneOption = false } = {}) => [
  ...[CREATE_NEW_OPTION],
  ...(addNoneOption ? [NO_VALUE_SELECTED_OPTION] : []),
  ...map(nodes, node => ({
    label: get(node, 'ivrNode.name', ''),
    value: get(node, 'ivrNode.id', ''),
  })),
];

const usePanelContent = ({
  treeData,
  setNodeIdForDeleting,
  ivrErrors,
  searchText,
  parentRef,
  onNodeSelected,
  ivrConfig,
  isSaveInProgress,
  isEditingAllowed,
  currentComponentOption,
  currentComponentId,
}) => {
  const nodeListMap = get(ivrConfig, 'nodesMap', {});
  const readOnlyMode = !isEditingAllowed;
  const disabled = readOnlyMode || isSaveInProgress;
  const currentNode = get(nodeListMap, currentComponentId);

  const nodeList = getNodeListOptions(
    sortBy(get(ivrConfig, 'nodesMap', {}), node => get(node, 'ivrNode.name')),
    { addNoneOption: true }
  );

  if (currentComponentId === START_NODE_ID) {
    return {
      'data-aid': START_NODE_ID,
      label: 'Start',
      description: 'Where the caller will start in the IVR',
      content: (
        <NodeContainer parentRef={parentRef}>
          <StartNode attr="rootNodeId" nodeList={nodeList} disabled={disabled} onSelectNode={onNodeSelected} />
        </NodeContainer>
      ),
    };
  }

  if (currentComponentId === NODE_LIST_ID || isEmpty(nodeListMap) || !currentNode) {
    return {
      'data-aid': 'all-nodes-list',
      content: (
        <NodeList
          nodes={nodeListMap}
          searchText={searchText}
          onListItemClick={nodeId => onNodeSelected(nodeId)}
          ivrErrors={ivrErrors}
        />
      ),
      readOnly: false,
    };
  }

  const nodeId = get(currentNode, 'ivrNode.id');
  const nodeType = get(currentNode, 'type');

  const attrNodeType = currentComponentId.startsWith('EDITABLE_')
    ? NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT
    : nodeType;

  const { label, description } = NODES_MAPPER[attrNodeType];
  const NodeComponent = NODE_COMPONENT_MAPPER[attrNodeType];
  const componentAttr = nodeType ? `nodesMap.${currentComponentId}.ivrNode` : '';

  const index: number =
    (get(currentComponentOption, 'data.isError') && SIDE_PANEL_ROW_TYPES.ERROR_ROW) ||
    (get(currentComponentOption, 'data.isNotMatch') && SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW) ||
    get(currentComponentOption, 'data.optionIndex');

  const deleteNodeButton = !disabled && (
    <DeleteNodeButton nodeId={nodeId} treeData={treeData} onDelete={() => setNodeIdForDeleting(currentComponentId)} />
  );

  return {
    'data-aid': 'ivrNode',
    content: (
      <NodeContainer key={nodeId} parentRef={parentRef} scrollToOption={!isNil(index)}>
        <NodeComponent
          attr={componentAttr}
          disabled={disabled}
          key={nodeId}
          nodeList={nodeList}
          onSelectNode={onNodeSelected}
          selectedOptionIndex={index}
        />
        {deleteNodeButton}
      </NodeContainer>
    ),
    description,
    label,
    nodeType,
    readOnlyMode,
  };
};

type SidePanelProps = {
  currentComponentId: string;
  currentComponentOption: string;
  isEditingAllowed: boolean;
  isSaveInProgress: boolean;
  ivrConfig?: IvrConfig;
  ivrErrors: any[];
  setCurrentComponentOption: Function;
  setNodeIdForDeleting: Function;
  treeData: TreeData | null;
};

const SidePanel: FC<SidePanelProps> = ({
  currentComponentId,
  currentComponentOption,
  isEditingAllowed,
  isSaveInProgress,
  ivrConfig,
  ivrErrors,
  setCurrentComponentOption,
  setNodeIdForDeleting,
  treeData,
}) => {
  const [searchText, setSearchText] = useState('');
  const sidePanelRef = useRef<any>();
  const { onNodeSelected } = useContext(SidePanelContext);

  const onSearchInputChange = ev => {
    setSearchText(ev.target.value);
    onNodeSelected(NODE_LIST_ID);
    setCurrentComponentOption(null);
  };

  const onSearchInputFocus = () => {
    if (currentComponentId !== NODE_LIST_ID) {
      onNodeSelected(NODE_LIST_ID);
    }
  };

  const panelContent = usePanelContent({
    currentComponentId,
    currentComponentOption,
    isEditingAllowed,
    isSaveInProgress,
    ivrConfig,
    ivrErrors,
    onNodeSelected,
    parentRef: sidePanelRef,
    searchText,
    setNodeIdForDeleting,
    treeData,
  });

  return (
    <SidePanelWrapper data-aid="node-config" ref={sidePanelRef}>
      <SidePanelLayout
        onSearchInputChange={onSearchInputChange}
        onSearchInputFocus={onSearchInputFocus}
        searchTextState={[searchText, setSearchText]}
        {...panelContent}
      />
    </SidePanelWrapper>
  );
};

const SidePanelWrapper = styled.div`
  background-color: ${p => p.theme.colors.white};
  bottom: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
  position: absolute;
  margin: 0 auto;
  overflow: auto;
  right: 0;
  top: 0;
  width: ${SIDE_PANEL_WIDTH};
`;

export default SidePanel;
